<script setup lang="ts">
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';

const {
  data,
  disableAddAndUpdateButton,
  addNumber,
  isNumberValid,
  isNumberAlreadyUsed,
} = useMobileNumberHandler();

const { $t } = useNuxtApp();
const site = useSiteStore();

const form = ref<IFormBuilderReference>(null);

const emits = defineEmits<{
  (e: 'update'): void;
  (e: 'clear-errors'): void;
}>();

const model = ref<{ newNumber: string }>({ newNumber: data.newNumber });

const convertNumber = (newNumber: string) => {
  return (newNumber.charAt(0) === '0' || newNumber.charAt(0) === '+') &&
    newNumber?.length > 1
    ? `${site?.getDialingCode?.replace('+', '')}${newNumber.slice(1)}`
    : `${site?.getDialingCode?.replace('+', '')}${newNumber}`;
};

const addNumberHandler = async () => {
  emits('clear-errors');
  if (!isNumberValid(data.newNumber) || disableAddAndUpdateButton.value) {
    data.errorCode = isNumberAlreadyUsed(data.newNumber)
      ? 'number-already-added'
      : 'invalid-number';
    return;
  }
  const response = await addNumber(data.newNumber);

  if (response?.isSuccessful) {
    emits('update');
    site.toggleSiteNotice({
      severity: 'success',
      heading: $t('success'),
      content: { data: $t(`pers-details-updated-succ`) },
      timeout: 3000,
      enabled: true,
    });
    if (!!form.value?.formErrors) form.value.formErrors['newNumber'] = '';
    data.errorCode = '';
    data.errorRef = '';
  } else {
    if (!!form.value?.formErrors)
      form.value.formErrors['newNumber'] = `update-details-${data.errorCode}`;
  }
};

watch(
  () => model.value.newNumber,
  () => {
    data.newNumber = convertNumber(model.value.newNumber);
    data.errorCode = '';
    data.errorRef = '';
  },
);
</script>

<template>
  <div v-show="!!form && !form.isEmpty">
    <p class="text-xs capitalize py-2">
      {{ $t('add-an-additional-phone-number') }}
    </p>
    <UserInterfaceDynamicFormBuilder
      ref="form"
      v-model="model"
      template-name="add number"
      @updated="emits('update')"
    >
      <template #input-suffix>
        <DesignSystemButton
          padding="wide-short"
          rounding="md"
          class="absolute right-2 top-1.5 h-[75%]"
          :disabled="
            !(form.validated && !form.invalid) || disableAddAndUpdateButton
          "
          @keyup.enter.prevent="addNumberHandler"
          @click="addNumberHandler"
        >
          {{ $t('add-number') }}
        </DesignSystemButton>
      </template>
    </UserInterfaceDynamicFormBuilder>
  </div>
</template>
